import { useCallback } from "react";
import { useAuth } from "src/core/hooks/useAuth";
import { useOnError } from "src/core/hooks/useOnError";
import { globalManualAdjustmentsService } from "src/core/services/globalManualAdjustments/globalManualAdjustmentsService";

export function useDataManageMethods<T extends { id: number }>(
  dataItems: T[] | undefined,
  setDataItems: (items: T[]) => void,
  setFilteredList: (items: T[]) => void,

  setTableLoading: (loading: boolean) => void,
  clearFilter?: () => void,
  setUnsavedRecord?: (record?: T) => void,
  unsavedRecord?: T | undefined,
) {
  const { accessToken } = useAuth();
  const { onError } = useOnError();

  /**
   * Loads records into state
   */
  const loadRecords = useCallback(async () => {
    setTableLoading(true);

    const response = (await globalManualAdjustmentsService.getRecords(
      accessToken,
    )) as T[];

    setDataItems(response);
    setTableLoading(false);
  }, [accessToken, setDataItems, setTableLoading]);

  /**
   * Reloads data and clears filters
   */
  const reloadData = useCallback(async () => {
    setTableLoading(true);

    const response = (await globalManualAdjustmentsService.getRecords(
      accessToken,
    )) as T[];

    clearFilter?.();

    setDataItems(response);
    setTableLoading(false);
  }, [accessToken, clearFilter, setDataItems, setTableLoading]);

  /**
   * Saves record and replaces with new fetched version
   */
  const saveRecord = useCallback(
    async (
      record: T,
      property: string,
      value: string | number | undefined | boolean,
    ) => {
      if (dataItems) {
        if (record[property as keyof T] === value) {
          return;
        }

        const recordUpdateData = {
          ...record,
          [property]: value,
          isInAction: true,
        };

        if (record.id > 0) {
          // DB record update
          let byResponseUpdatedDataItems: T[] = [...dataItems];

          const updatedItemIndex = byResponseUpdatedDataItems.findIndex(
            (y) => y.id === record.id,
          );

          byResponseUpdatedDataItems.splice(
            updatedItemIndex,
            1,
            recordUpdateData,
          );

          setFilteredList(byResponseUpdatedDataItems);

          await globalManualAdjustmentsService
            .saveRecord(accessToken, recordUpdateData)
            .catch(() => {
              onError();
            });

          const response = (await globalManualAdjustmentsService.getRecord(
            accessToken,
            record.id,
          )) as T;

          byResponseUpdatedDataItems = [...dataItems];

          byResponseUpdatedDataItems.splice(updatedItemIndex, 1, response);
          setFilteredList(byResponseUpdatedDataItems);
        } else {
          if (unsavedRecord) {
            setUnsavedRecord?.({ ...unsavedRecord, [property]: value });
          }
        }
      }
    },
    [
      accessToken,
      dataItems,
      onError,
      setFilteredList,
      setUnsavedRecord,
      unsavedRecord,
    ],
  );

  const deleteRecord = useCallback(
    async (record: T): Promise<void> => {
      if (dataItems) {
        setTableLoading(true);

        await globalManualAdjustmentsService
          .deleteRecord(accessToken, record.id)
          .catch(() => {
            onError();
          });

        setDataItems([...dataItems.filter((item) => item.id !== record.id)]);

        setTableLoading(false);
      }
    },
    [accessToken, dataItems, onError, setDataItems, setTableLoading],
  );

  const duplicateRecord = useCallback(
    async (record: T): Promise<void> => {
      if (dataItems) {
        setTableLoading(true);

        let byResponseUpdatedDataItems: T[] = [...dataItems];

        const updatedItemIndex = byResponseUpdatedDataItems.findIndex(
          (y) => y.id === record.id,
        );

        byResponseUpdatedDataItems.splice(updatedItemIndex, 1, {
          ...dataItems[updatedItemIndex],
          isInAction: true,
        });

        setDataItems(byResponseUpdatedDataItems);

        await globalManualAdjustmentsService
          .duplicateRecord(accessToken, record.id)
          .catch(() => {
            onError();
          });

        await reloadData();

        setTableLoading(false);
      }
    },
    [
      accessToken,
      dataItems,
      onError,
      reloadData,
      setDataItems,
      setTableLoading,
    ],
  );

  const deleteUnsavedRecord = useCallback(() => {
    if (dataItems) {
      setDataItems(dataItems.filter((item) => item.id > 0));
      setUnsavedRecord?.(undefined);
    }
  }, [dataItems, setDataItems, setUnsavedRecord]);

  const createRecord = useCallback(
    async (data: T) => {
      setTableLoading(true);

      await globalManualAdjustmentsService
        .createRecord(accessToken, data)
        .catch(() => {
          onError();
        });

      await reloadData();
      setUnsavedRecord?.(undefined);
      setTableLoading(false);
    },
    [accessToken, onError, reloadData, setTableLoading, setUnsavedRecord],
  );

  /**
   * Handles add button click. Returns a new record for saving.
   */
  const handleAddClick = useCallback(
    async (currencyCode?: string) => {
      let newRecord: T = {
        id: -1,
        currencyCode: currencyCode,
        isInAction: true,
      } as unknown as T;

      if (dataItems) {
        const existingIndex = dataItems.findIndex((x) => x.id === -1);

        if (existingIndex === -1) {
          setDataItems([...[newRecord], ...dataItems]);
          setUnsavedRecord?.(newRecord);
        } else {
          newRecord = dataItems[existingIndex];
        }
      }

      await createRecord(newRecord);
    },
    [createRecord, dataItems, setDataItems, setUnsavedRecord],
  );

  const deactivateAlert = useCallback(
    async (data: T, alertId: number) => {
      setTableLoading(true);

      await globalManualAdjustmentsService
        .deactivateRecordAlert(accessToken, data.id, alertId)
        .catch(() => {
          onError();
        });

      await reloadData();

      setTableLoading(false);
    },
    [accessToken, onError, reloadData, setTableLoading],
  );

  const onExportClick = useCallback(
    async (filteredList: T[]) => {
      let filteredIds: number[] = [];

      filteredList.forEach((item) => {
        filteredIds.push(item.id);
      });

      await globalManualAdjustmentsService
        .downloadGlobalAdjustmentData(accessToken, filteredIds)
        .catch(() => {
          onError();
        });
    },
    [accessToken, onError],
  );

  return {
    saveRecord,
    deleteRecord,
    loadRecords,
    deleteUnsavedRecord,
    handleAddClick,
    reloadData,
    createRecord,
    deactivateAlert,
    onExportClick,
    duplicateRecord,
  };
}
